@if (subscriptionResource.isLoading() && !subscriptionResource.hasValue()) {
  <p-message severity="info" text="Loading billing period…" />
} @else if (subscriptionResource.error()) {
  <p-message severity="error" text="Could not load this membership's billing details." />
  <p-button
    label="Try again"
    data-testid="membership-expiration-retry-load"
    [text]="true"
    (onClick)="subscriptionResource.reload()"
  />
} @else if (subscriptionResource.value(); as data) {
  @if (data.membership.status === MembershipStatus.Cancelled) {
    <p-message severity="warn" text="This membership is cancelled; its expiry can't be moved." />
  } @else if (data.subscription; as subscription) {
    <dl class="period">
      <dt>Current period ends</dt>
      <dd data-testid="membership-expiration-current-period-end">
        {{ subscription.currentPeriodEndUtc | date: 'medium' : 'UTC' }} UTC
      </dd>
      <dt>Access ends</dt>
      <dd data-testid="membership-expiration-access-end">
        {{ subscription.effectiveAccessEndUtc | date: 'medium' : 'UTC' }} UTC
      </dd>
      <dt>Entitlements expiry</dt>
      <dd data-testid="membership-expiration-entitlements-expiry">
        @if (data.membership.currentExpirationUtc) {
          {{ data.membership.currentExpirationUtc | date: 'medium' : 'UTC' }} UTC
        } @else {
          Not set
        }
      </dd>
      <dt>Subscription status</dt>
      <dd>{{ subscription.status }}</dd>
      <dt>Billed through Square</dt>
      <dd>{{ subscription.isProviderBacked ? 'Yes' : 'No' }}</dd>
    </dl>

    @if (canSyncEntitlements()) {
      <p-message
        severity="warn"
        text="AccessManagement's entitlements expire after the end an admin shortened this period to, so AccessManagement may never have received that change."
      />
      <p-button
        label="Sync entitlements"
        data-testid="membership-expiration-sync-entitlements"
        [disabled]="submitting()"
        [loading]="syncing()"
        (onClick)="syncEntitlements()"
      />
    } @else if (unsyncedShortenedEndHasPassed()) {
      <p-message severity="info" [text]="shortenedEndPassedNote" />
    } @else if (entitlementsDrift() === 'behindBilling') {
      <p-message
        severity="warn"
        text="Entitlements expiry is behind PaymentsEngine's access end. This usually follows a missed renewal event, and the next renewal will bring it back in line."
      />
    }

    @if (!subscription.isProviderBacked) {
      <p class="note" role="note">
        <i class="pi pi-exclamation-triangle warn" aria-hidden="true"></i>
        <span>
          No Square subscription backs this. Cancellation will still land on the new date, but no invoice will fire, so
          a scheduled upgrade or downgrade will not apply.
        </span>
      </p>
    }

    @if (subscription.isProviderBacked && !squareRenewalsReachThisEnvironment) {
      <p class="note" role="note">
        <i class="pi pi-exclamation-triangle warn" aria-hidden="true"></i>
        <span>
          Square's webhooks don't reach this environment, so no renewal is recorded after a shortening: a scheduled plan
          change never applies and benefits lapse. Test upgrades and downgrades in demo.
        </span>
      </p>
    }

    @if (subscription.status === 'PendingCancellation') {
      <p class="note" role="note">
        <i class="pi pi-info-circle" aria-hidden="true"></i>
        @if (subscription.isProviderBacked) {
          <span>
            A cancellation is already pending. Square keeps its current schedule; only the access end moves. After this,
            the owner can't reverse the cancellation.
          </span>
        } @else {
          <span>A cancellation is already pending.</span>
        }
      </p>
    } @else if (subscription.isProviderBacked) {
      <p class="note" role="note">
        <i class="pi pi-info-circle" aria-hidden="true"></i>
        <span>
          To test a cancellation, have the owner cancel first, then shorten. Cancelling after a shortening leaves the
          two engines disagreeing on when access ends.
        </span>
      </p>
    }

    @if (!canShortenStatus()) {
      <p class="note" role="note">
        <i class="pi pi-info-circle" aria-hidden="true"></i>
        <span>
          PaymentsEngine only accepts a period change while the subscription is Active, past due, or pending
          cancellation.
        </span>
      </p>
    } @else if (!hasRoomToShorten()) {
      <p class="note" role="note">
        <i class="pi pi-info-circle" aria-hidden="true"></i>
        <span>
          There is no earlier day to move this period end to: it ends within a day or has already ended, or the only
          days left are the 29th to the 31st, which aren't in every month.
        </span>
      </p>
    } @else {
      <div class="form">
        <label [attr.for]="expirationDateInputId()">New period end (UTC)</label>
        <p-datepicker
          data-testid="membership-expiration-date"
          [inputId]="expirationDateInputId()"
          [ngModel]="selectedDate()"
          [minDate]="minDate()"
          [maxDate]="maxDate()"
          [disabledDates]="disabledDates()"
          [showIcon]="true"
          (ngModelChange)="selectedDate.set($event)"
        />
        <small class="help-text" [id]="periodDriftHintId()">
          Square bills on the same day each month; days 29-31 drift in shorter months.
        </small>

        <label [attr.for]="expirationReasonInputId()">Reason</label>
        <input
          pInputText
          required
          data-testid="membership-expiration-reason"
          maxlength="512"
          placeholder="e.g. QA: apply scheduled downgrade"
          [id]="expirationReasonInputId()"
          [attr.aria-describedby]="reasonHintId()"
          [ngModel]="reason()"
          (ngModelChange)="reason.set($event)"
        />
        <small class="help-text" [id]="reasonHintId()">
          Don't include customer details — this is stored in audit rows.
        </small>

        <p-button
          label="Shorten period"
          data-testid="membership-expiration-shorten"
          [disabled]="!canSubmit()"
          [loading]="shortening()"
          (onClick)="confirmShorten()"
        />
      </div>
    }
  } @else {
    <p-message severity="warn" text="This membership has no subscription, so there is no billing period to shorten." />
  }
}

<div class="result-region" aria-live="polite">
  @if (result(); as r) {
    <ul class="result">
      <li data-testid="membership-expiration-result-payments">
        Payments & Square:
        @if (r.action === 'sync') {
          <strong>not changed</strong>
        } @else {
          <strong>{{ r.payments }}</strong>
          @if (r.paymentsReason) {
            — {{ r.paymentsReason }}
          }
          @if (r.committedPeriodEndUtc) {
            — period now ends {{ r.committedPeriodEndUtc | date: 'medium' : 'UTC' }} UTC
            @if (r.squareUpdated === false) {
              (Square not changed)
            }
          }
        }
      </li>
      <li data-testid="membership-expiration-result-entitlements">
        Entitlements:
        <strong>{{ r.entitlements }}</strong>
        @if (r.entitlementsReason) {
          — {{ r.entitlementsReason }}
        }
      </li>
    </ul>
    @if (r.entitlements === 'failed') {
      @if (r.action === 'sync') {
        <p-message
          severity="warn"
          data-testid="membership-expiration-entitlements-failed"
          text="Entitlements weren't synced."
        />
      } @else {
        <p-message
          severity="warn"
          data-testid="membership-expiration-entitlements-failed"
          text="Billing moved but entitlements did not."
        />
      }
      @if (canRetryEntitlements()) {
        <p-button
          label="Retry entitlements"
          data-testid="membership-expiration-retry-entitlements"
          [disabled]="submitting() || subscriptionResource.isLoading()"
          [loading]="retrying()"
          (onClick)="retryEntitlements()"
        />
      } @else if (entitlementsExpireBeforeCommittedEnd()) {
        <p-message severity="info" [text]="entitlementsAlreadyEarlierNote" />
      } @else {
        <p-message severity="info" [text]="shortenedEndPassedNote" />
      }
    }
  }
</div>
